import {DragEndEvent} from '@dnd-kit/core';
import {arrayMove} from '@dnd-kit/sortable';

import {ReactNode, HTMLAttributes} from 'react';

import {isNil, isNotNil} from 'ramda';

import {buildSortableItemId} from '../utils/buildSortableItemId';
import {parseDndItemId} from '../utils/parseDndItemId';
import {DnDContext} from './DnDContext';
import {Sortable} from './Sortable';
import {SortableWrapper} from './SortableWrapper';

interface SortableCardListItem {
  id: string;
  url?: string;
}

export interface SortableCardListMove<T> {
  /** The dragged item. */
  item: T;
  /** Index of the dragged item in the reordered list. */
  toIndex: number;
}

export interface SortableCardListProps<T extends SortableCardListItem> {
  containerId: string;
  items: T[];
  onReorder: (reorderedItems: T[], move: SortableCardListMove<T>) => void;
  renderCard: (item: T, index: number, handleProps?: HTMLAttributes<unknown>) => ReactNode;
  withHandle?: boolean;
  disableDropAnimation?: boolean;
  disabledDrag?: boolean;
}

const FULL_WIDTH_STYLE = {width: '100%'};

export function SortableCardList<T extends SortableCardListItem>(props: SortableCardListProps<T>) {
  const onDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (isNil(over) || active.id === over.id) {
      return;
    }

    const {position: fromIndex} = parseDndItemId(active.id);
    const {position: toIndex} = parseDndItemId(over.id);

    const movedItem = isNotNil(fromIndex) ? props.items[fromIndex] : undefined;

    if (isNotNil(fromIndex) && isNotNil(toIndex) && isNotNil(movedItem)) {
      const reorderedDefinitions = arrayMove(props.items, fromIndex, toIndex);

      props.onReorder(reorderedDefinitions, {item: movedItem, toIndex});
    }
  };

  return (
    <DnDContext onDragEnd={onDragEnd}>
      <SortableWrapper
        containerId={props.containerId}
        items={props.items.map((item) => ({
          id: item.id,
          url: item?.url ?? item.id,
        }))}
      >
        {props.items.map((item, index) => (
          // eslint-disable-next-line react/forbid-dom-props
          <div style={FULL_WIDTH_STYLE} key={item.id}>
            <Sortable
              id={buildSortableItemId(item.id, props.containerId, item.id, index)}
              withHandle={props.withHandle}
              disableDropAnimation={props.disableDropAnimation}
              disabledDrag={props.disabledDrag}
            >
              {({handleProps}) => props.renderCard(item, index, handleProps)}
            </Sortable>
          </div>
        ))}
      </SortableWrapper>
    </DnDContext>
  );
}
